import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  List,
  ListItem,
  Alert,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  VideocamOutlined as VideoIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import {
  AvailableMeeting,
  getAvailableMeetings,
  buildMeetingUrl,
  formatMeetingTime,
  getRoleDisplayText,
  getMeetingStatusInfo,
  getEventTypeInfo
} from '../../services/meetingService';
import { nextPreload, useProxyWatch } from '@fanfanlo';
import { user } from '@app/model';
import { autoWebViewJs } from '@droid/android';
import { useRouter } from 'next/router';
import Link from 'next/link';

interface MeetingListProps {
  maxItemLength?:number;
  className?: string;
  onMeetingJoin?: (meeting: AvailableMeeting) => void;
}

const MeetingList: React.FC<MeetingListProps> = ({ maxItemLength, className, onMeetingJoin }) => {
  const [meetings, setMeetings] = useState<AvailableMeeting[]>([]);
  const [realMeetings, setRealMeetings] = useState<AvailableMeeting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const [token] = useProxyWatch(user, 'data.storeData.user_token', user.data.storeData.user_token);

  // Check if running in Android WebView environment
  const isAndroidWebView = (): boolean => {
    try {
      // Check if autoWebViewJs is available
      const hasAutoWebViewJs = !!(autoWebViewJs && typeof autoWebViewJs.callScript === 'function');
      return hasAutoWebViewJs;
    } catch (e) {
      console.warn('🔍 MeetingList: Error checking Android WebView environment:', e);
      return false;
    }
  };
  // 加载会议列表
  const loadMeetings = async () => {
    setLoading(true);
    setError(null);
    try {
      const meetingList = await getAvailableMeetings();
      meetingList.sort((a, b) => {
        // 按创建时间降序排序
        return b.created_at - a.created_at;
      });
      setRealMeetings([...meetingList]);
      if(maxItemLength && meetingList.length > maxItemLength) {
        // 如果有最大长度限制，截断列表

        meetingList.length = maxItemLength;
      }
      setMeetings(meetingList);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '加载会议列表失败';
      setError(errorMsg);
      console.error('📺 Failed to load meetings:', err);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    if(!token)return;
    loadMeetings();
  }, [token]);

  // 加入会议
  const handleJoinMeeting = (meeting: AvailableMeeting) => {
    console.log('📺 Joining meeting:', meeting);
    

      // React.useEffect(()=>{
      //   nextPreload(router, '/join-call')
      // },[])

      console.log('onMettingJoin_fn', onMeetingJoin)
    if (onMeetingJoin) {
      onMeetingJoin(meeting);
    } else {
      // 默认行为：使用路由导航而不是页面重载
      const queryParams = new URLSearchParams({
        code: meeting.short_code,
        role: meeting.role,
        ...(meeting.event_type && { eventType: meeting.event_type })
      });
      const url = `/join-call2?${queryParams.toString()}`;
      console.log('handleJoinMeeting Navigating to URL:', url);
      router.push(url);

    }
  };

  // 渲染单个会议项
  const renderMeetingItem = (meeting: AvailableMeeting) => {
    const statusInfo = getMeetingStatusInfo(meeting.status);
    const roleText = getRoleDisplayText(meeting.role);
    const eventTypeInfo = getEventTypeInfo(meeting.event_type || 'security');
    const timeText = formatMeetingTime(meeting.created_at);
    return (
      <Card 
        key={meeting.event_uuid} 
        sx={{ 
          mb: 2, 
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)'
          }
        }}
        onClick={() => handleJoinMeeting(meeting)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* 发起者头像 */}
            <Avatar 
              src={meeting.initiator_info.avatar_url} 
              sx={{ width: 48, height: 48 }}
            >
              {meeting.initiator_info.display_name.charAt(0)}
            </Avatar>

            {/* 会议信息 */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  {meeting.initiator_info.display_name}
                </Typography>
                <Chip 
                  label={`${eventTypeInfo.icon} ${eventTypeInfo.text}`}
                  size="small"
                  sx={{ 
                    fontSize: '0.75rem',
                    backgroundColor: eventTypeInfo.color,
                    color: 'white'
                  }}
                />
                <Chip 
                  label={roleText}
                  size="small"
                  color={meeting.role === 'initiator' ? 'primary' : 'secondary'}
                  sx={{ fontSize: '0.75rem' }}
                />
                <Chip 
                  label={statusInfo.text}
                  size="small"
                  sx={{ 
                    fontSize: '0.75rem',
                    backgroundColor: statusInfo.color,
                    color: 'white'
                  }}
                />
              </Box>

              <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VideoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {meeting.short_code}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {timeText}
                  </Typography>
                </Box>
              </Stack>

              {meeting.access_mode && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  访问模式: {meeting.access_mode}
                </Typography>
              )}
            </Box>

            {/* 加入按钮 */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<VideoIcon />}
                sx={{ minWidth: 100, whiteSpace: 'nowrap' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleJoinMeeting(meeting);
                }}
              >
                加入会议
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

 if(!token){
  return <></>
 }
 if(realMeetings.length === 0){
  return <></>
 }
  return (
    <Box className={className} sx={{ width: '100%', mx: 'auto', p: 0 }}>
      {/* 标题和刷新按钮 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          近期会议
        </Typography>
        {maxItemLength && realMeetings.length > maxItemLength && <Link href="/meetings">全部</Link>}
        <IconButton 
          onClick={loadMeetings} 
          disabled={loading}
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': { backgroundColor: 'primary.dark' }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* 错误信息 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button 
            size="small" 
            onClick={loadMeetings} 
            sx={{ ml: 1 }}
          >
            重试
          </Button>
        </Alert>
      )}

      {/* 加载状态 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            加载会议列表中...
          </Typography>
        </Box>
      )}

      {/* 会议列表 */}
      {!loading && !error && (
        <>
          {meetings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              {/* <VideoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} /> */}
              {/* <Typography variant="h6" color="text.secondary" gutterBottom>
                暂无可参与的会议
              </Typography> */}
              {/* <Typography variant="body2" color="text.secondary">
                发起新的紧急呼救或等待他人邀请您参与会议
              </Typography> */}
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                共找到 {meetings.length} 个可参与的会议
              </Typography>
              {meetings.map(renderMeetingItem)}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MeetingList;